import type { PlayerPushSubscription } from '../api/public.ts'

const serviceWorkerPath = '/player-push-sw.js'
const serviceWorkerScope = '/'
const deviceIDStorageKey = 'dota688.web-push.device-id.v1'
const sessionStorageKey = 'dota688.web-push.session.v1'
const enableIntentStorageKey = 'dota688.web-push.enable-intent.v1'
const legacyActorStorageKey = 'dota688.web-push.actor.v1'
const changeStorageKey = 'dota688.web-push.changed.v1'
const changeChannelName = 'dota688.player-push.v1'
const lifecycleLockName = 'dota688.player-push-lifecycle.v1'
const deviceIDPattern = /^[0-9a-f]{16}$/
const sessionTokenPattern = /^[0-9a-f]{16}$/
const serviceWorkerOperationTimeoutMS = 5_000

export type PlayerPushEnableIntent = {
  id: string
  sessionToken: string
}

export type PlayerWebPushBrowser = {
  supported: () => boolean
  permission: () => NotificationPermission | 'unsupported'
  requestPermission: () => Promise<NotificationPermission>
  registration: () => Promise<ServiceWorkerRegistration | null>
  register: () => Promise<ServiceWorkerRegistration>
  addMessageListener: (listener: (event: MessageEvent) => void) => void
  removeMessageListener: (listener: (event: MessageEvent) => void) => void
  subscribeChanges: (listener: () => void) => () => void
  broadcastChange: () => void
  withLifecycleLock: <T>(task: () => Promise<T>) => Promise<T>
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
  randomBytes: (length: number) => Uint8Array
}

export function defaultPlayerWebPushBrowser(): PlayerWebPushBrowser {
  let changeChannel: BroadcastChannel | null = null
  return {
    supported: () => (
      typeof window !== 'undefined' &&
      window.isSecureContext &&
      typeof Notification !== 'undefined' &&
      'serviceWorker' in navigator &&
      typeof PushManager !== 'undefined' &&
      'locks' in navigator &&
      typeof navigator.locks?.request === 'function'
    ),
    permission: () => typeof Notification === 'undefined'
      ? 'unsupported'
      : Notification.permission,
    requestPermission: () => Notification.requestPermission(),
    registration: async () => (
      await navigator.serviceWorker.getRegistration(serviceWorkerScope)
    ) ?? null,
    register: async () => {
      const registration = await navigator.serviceWorker.register(serviceWorkerPath, {
        scope: serviceWorkerScope,
        type: 'module',
        updateViaCache: 'none',
      })
      if (registration.active) return registration
      const ready = await navigator.serviceWorker.ready
      return ready.scope === registration.scope ? ready : registration
    },
    addMessageListener: listener => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.addEventListener('message', listener)
      }
    },
    removeMessageListener: listener => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', listener)
      }
    },
    subscribeChanges: listener => {
      changeChannel = typeof BroadcastChannel === 'undefined'
        ? null
        : new BroadcastChannel(changeChannelName)
      const handleChannelMessage = (event: MessageEvent) => {
        if (
          event.data &&
          typeof event.data === 'object' &&
          !Array.isArray(event.data) &&
          Object.keys(event.data).length === 1 &&
          event.data.type === 'dota688.player-push-changed'
        ) {
          listener()
        }
      }
      const handleStorage = (event: StorageEvent) => {
        if (event.key === sessionStorageKey || event.key === changeStorageKey) listener()
      }
      const handleControllerChange = () => listener()
      const handleVisibilityChange = () => {
        if (!document.hidden) listener()
      }
      changeChannel?.addEventListener('message', handleChannelMessage)
      if (!changeChannel) window.addEventListener('storage', handleStorage)
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange)
      }
      document.addEventListener('visibilitychange', handleVisibilityChange)
      return () => {
        changeChannel?.removeEventListener('message', handleChannelMessage)
        if (!changeChannel) window.removeEventListener('storage', handleStorage)
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange)
        }
        document.removeEventListener('visibilitychange', handleVisibilityChange)
        changeChannel?.close()
        changeChannel = null
      }
    },
    broadcastChange: () => {
      if (changeChannel) {
        try {
          changeChannel.postMessage({ type: 'dota688.player-push-changed' })
        } catch {
          // The page may be disposing while serialized cleanup finishes.
        }
        return
      }
      try {
        window.localStorage.setItem(
          changeStorageKey,
          `${Date.now()}:${Math.random()}`,
        )
      } catch {
        // Other tabs still reconcile on visibility and controller changes.
      }
    },
    withLifecycleLock: async task => {
      if ('locks' in navigator && typeof navigator.locks?.request === 'function') {
        return await navigator.locks.request(
          lifecycleLockName,
          { mode: 'exclusive' },
          async () => await task(),
        )
      }
      return await task()
    },
    storage: {
      getItem: key => window.localStorage.getItem(key),
      setItem: (key, value) => window.localStorage.setItem(key, value),
      removeItem: key => window.localStorage.removeItem(key),
    },
    randomBytes: length => crypto.getRandomValues(new Uint8Array(length)),
  }
}

export async function ensurePlayerPushRegistration(
  browser: PlayerWebPushBrowser,
  timeoutMS = serviceWorkerOperationTimeoutMS,
): Promise<ServiceWorkerRegistration> {
  const existing = await boundedServiceWorkerOperation(
    browser.registration(),
    timeoutMS,
  )
  if (existing && !playerPushRegistration(existing)) {
    throw new Error('push_service_worker_conflict')
  }
  const registration = existing?.active
    ? existing
    : await boundedServiceWorkerOperation(browser.register(), timeoutMS)
  if (!playerPushRegistration(registration)) {
    throw new Error('push_service_worker_conflict')
  }
  return registration
}

async function boundedServiceWorkerOperation<T>(
  promise: Promise<T>,
  timeoutMS: number,
): Promise<T> {
  let timeout: ReturnType<typeof setTimeout> | undefined
  const deadline = new Promise<never>((_resolve, reject) => {
    timeout = setTimeout(() => {
      const error = new Error('push_service_worker_timeout')
      error.name = 'AbortError'
      reject(error)
    }, Math.max(1, timeoutMS))
  })
  try {
    return await Promise.race([promise, deadline])
  } finally {
    if (timeout !== undefined) clearTimeout(timeout)
  }
}

export function playerPushRegistration(
  registration: ServiceWorkerRegistration,
): boolean {
  const worker = registration.active ?? registration.waiting ?? registration.installing
  if (!worker) return true
  try {
    return new URL(worker.scriptURL).pathname === serviceWorkerPath
  } catch {
    return false
  }
}

export function postRevisionAuthority(
  registration: ServiceWorkerRegistration | null,
): void {
  if (!registration || !playerPushRegistration(registration)) return
  registration.active?.postMessage({ type: 'dota688.player-push-authority' })
}

export function persistentDeviceID(browser: PlayerWebPushBrowser): string {
  const current = browser.storage.getItem(deviceIDStorageKey)
  if (current && deviceIDPattern.test(current)) return current
  const generated = [...browser.randomBytes(8)]
    .map(value => value.toString(16).padStart(2, '0'))
    .join('')
  browser.storage.setItem(deviceIDStorageKey, generated)
  const persisted = browser.storage.getItem(deviceIDStorageKey)
  if (persisted && deviceIDPattern.test(persisted)) return persisted
  throw new Error('push_device_storage_unavailable')
}

export function createPlayerPushEnableIntent(
  browser: PlayerWebPushBrowser,
  sessionToken: string,
): PlayerPushEnableIntent {
  if (!sessionTokenPattern.test(sessionToken)) {
    throw new Error('push_config_invalid')
  }
  return {
    id: [...browser.randomBytes(8)]
      .map(value => value.toString(16).padStart(2, '0'))
      .join(''),
    sessionToken,
  }
}

export function readPlayerPushEnableIntent(
  browser: PlayerWebPushBrowser,
): PlayerPushEnableIntent | null {
  try {
    const raw = browser.storage.getItem(enableIntentStorageKey)
    if (!raw) return null
    const value = JSON.parse(raw) as unknown
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null
    const intent = value as Record<string, unknown>
    if (
      Object.keys(intent).sort().join(',') !== 'id,sessionToken' ||
      typeof intent.id !== 'string' ||
      !deviceIDPattern.test(intent.id) ||
      typeof intent.sessionToken !== 'string' ||
      !sessionTokenPattern.test(intent.sessionToken)
    ) return null
    return { id: intent.id, sessionToken: intent.sessionToken }
  } catch {
    return null
  }
}

export function writePlayerPushEnableIntent(
  browser: PlayerWebPushBrowser,
  intent: PlayerPushEnableIntent,
): void {
  browser.storage.setItem(enableIntentStorageKey, JSON.stringify(intent))
}

export function removePlayerPushEnableIntent(
  browser: PlayerWebPushBrowser,
  expected: Partial<PlayerPushEnableIntent>,
): boolean {
  const current = readPlayerPushEnableIntent(browser)
  if (!current) return false
  if (expected.id !== undefined && current.id !== expected.id) return false
  if (
    expected.sessionToken !== undefined &&
    current.sessionToken !== expected.sessionToken
  ) return false
  try {
    browser.storage.removeItem(enableIntentStorageKey)
    return true
  } catch {
    return false
  }
}

export function readStoredSession(browser: PlayerWebPushBrowser): string {
  try {
    return browser.storage.getItem(sessionStorageKey) ?? ''
  } catch {
    return ''
  }
}

export function removeLegacyStoredOwner(browser: PlayerWebPushBrowser): void {
  try {
    browser.storage.removeItem(legacyActorStorageKey)
  } catch {
    // Best-effort privacy cleanup for the pre-session owner marker.
  }
}

export function writeStoredSession(
  browser: PlayerWebPushBrowser,
  sessionToken: string,
): void {
  browser.storage.setItem(sessionStorageKey, sessionToken)
}

export function removeStoredSession(
  browser: PlayerWebPushBrowser,
  expectedSession?: string,
): void {
  try {
    if (
      expectedSession !== undefined &&
      browser.storage.getItem(sessionStorageKey) !== expectedSession
    ) return
    browser.storage.removeItem(sessionStorageKey)
  } catch {
    // A locally unsubscribed endpoint is already unable to receive pushes.
  }
}

export async function closePlayerPushNotifications(
  registration: ServiceWorkerRegistration | null,
): Promise<void> {
  if (!registration) return
  registration.active?.postMessage({ type: 'dota688.player-push-disable' })
  try {
    const notifications = await registration.getNotifications()
    for (const notification of notifications) {
      if (
        notification.data?.type === 'dota688.player-push' ||
        notification.data?.type === 'dota688.player-push-fallback'
      ) notification.close()
    }
  } catch {
    // Some browsers expose PushManager without notification enumeration.
  }
}

export function serializePushSubscription(
  subscription: Pick<PushSubscription, 'endpoint' | 'expirationTime' | 'toJSON'>,
): PlayerPushSubscription {
  const value = subscription.toJSON()
  const p256dh = value.keys?.p256dh
  const auth = value.keys?.auth
  if (
    typeof subscription.endpoint !== 'string' ||
    !subscription.endpoint ||
    typeof p256dh !== 'string' ||
    !p256dh ||
    typeof auth !== 'string' ||
    !auth
  ) {
    throw new Error('push_subscription_invalid')
  }
  const expirationTime = subscription.expirationTime
  return {
    endpoint: subscription.endpoint,
    ...(typeof expirationTime === 'number' && Number.isFinite(expirationTime)
      ? { expiration_time: new Date(expirationTime).toISOString() }
      : {}),
    keys: { p256dh, auth },
  }
}

export function decodeApplicationServerKey(value: string | undefined): Uint8Array {
  if (typeof value !== 'string' || !/^[A-Za-z0-9_-]{86,88}$/.test(value)) {
    throw new Error('push_application_server_key_invalid')
  }
  const padded = value.replace(/-/g, '+').replace(/_/g, '/') +
    '='.repeat((4 - value.length % 4) % 4)
  let binary: string
  try {
    binary = atob(padded)
  } catch {
    throw new Error('push_application_server_key_invalid')
  }
  const bytes = Uint8Array.from(binary, character => character.charCodeAt(0))
  if (bytes.length !== 65 || bytes[0] !== 4) {
    throw new Error('push_application_server_key_invalid')
  }
  return bytes
}

export function sameBytes(left: ArrayBuffer | null, right: Uint8Array): boolean {
  if (!left) return false
  const leftBytes = new Uint8Array(left)
  return leftBytes.length === right.length &&
    leftBytes.every((value, index) => value === right[index])
}

export function ownedArrayBuffer(value: Uint8Array): ArrayBuffer {
  const buffer = new ArrayBuffer(value.byteLength)
  new Uint8Array(buffer).set(value)
  return buffer
}
